// Self-service password change (#289), shared card (#382): rendered on the Account screen. The
// Settings "Password" section it also served was folded into Account in #1431, so Account is the
// only caller now. The server verifies the current password and revokes every other session;
// this form never sees more than the two password fields.
import React from "react";
import { Banner, Button, Card, FormField, Input } from "@/ds";
import { ApiError, cockpit } from "@/api";
import { Icon, PanelLabel } from "@/lib";
import { useT } from "../i18n";
import type { PushToast } from "@/notifications/toast-queue";

export interface AccountPasswordCardProps {
  pushToast: PushToast;
}

export function AccountPasswordCard({ pushToast }: AccountPasswordCardProps) {
  const t = useT();
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [passwordError, setPasswordError] = React.useState<string | null>(null);
  const [passwordBusy, setPasswordBusy] = React.useState(false);

  const savePassword = async () => {
    setPasswordError(null);
    if (newPassword !== confirmPassword) {
      setPasswordError(t("account.passwordMismatch"));
      return;
    }
    setPasswordBusy(true);
    try {
      await cockpit.changeOwnPassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      pushToast({ tone: "success", title: t("account.passwordSaved") });
    } catch (error) {
      setPasswordError(
        error instanceof ApiError && error.status === 401
          ? t("account.currentPasswordInvalid")
          : error instanceof Error && error.message
            ? error.message
            : t("common.error"),
      );
    } finally {
      setPasswordBusy(false);
    }
  };

  return (
    <Card>
      <PanelLabel icon="key">{t("account.password")}</PanelLabel>
      <p className="subtle management-copy" style={{ marginTop: 0 }}>{t("account.passwordHint")}</p>
      {passwordError && <Banner tone="danger" title={t("common.error")}>{passwordError}</Banner>}
      <div className="stack-sm account-form">
        <FormField label={t("account.currentPassword")} htmlFor="account-current-password" required>
          <Input
            id="account-current-password"
            type="password"
            value={currentPassword}
            autoComplete="current-password"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCurrentPassword(event.currentTarget.value)}
          />
        </FormField>
        <FormField
          label={t("account.newPassword")}
          htmlFor="account-new-password"
          helper={t("account.passwordRequirement")}
          required
        >
          <Input
            id="account-new-password"
            type="password"
            value={newPassword}
            minLength={10}
            autoComplete="new-password"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNewPassword(event.currentTarget.value)}
          />
        </FormField>
        <FormField label={t("account.confirmPassword")} htmlFor="account-confirm-password" required>
          <Input
            id="account-confirm-password"
            type="password"
            value={confirmPassword}
            minLength={10}
            autoComplete="new-password"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(event.currentTarget.value)}
          />
        </FormField>
        <div className="management-actions">
          <Button loading={passwordBusy}
            variant="primary"
            iconLeft={<Icon name="key" size={16} />}
            disabled={passwordBusy || !currentPassword || newPassword.length < 10 || !confirmPassword}
            onClick={() => { void savePassword(); }}
          >
            {t("account.savePassword")}
          </Button>
        </div>
      </div>
    </Card>
  );
}
